import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Checkbox } from '@/common/ui/checkbox'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import useMutationUpdateChannel from '@/modules/manage-player/api/hooks/useMutationUpdateChannel'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import { useEffect, useState } from 'react'
import { Pencil } from 'lucide-react'
import useQueryGetDepositPromotionNamesForSelectorChannel from '@/modules/promotion/api/hooks/useQueryGetDepositPromotionNamesForSelectorChannel'

interface Props {
	data: {
		id: string
		name: string
		cost?: number
		note?: string
		isGuest?: boolean
		promotionId?: string
	}
	onSuccess?: () => void
}

export const UpdateChannelFormModal = ({ data, onSuccess }: Props) => {
	const [open, setOpen] = useState(false)

	const formSchema = z.object({
		cost: z.number().min(0, 'ต้นทุนต้องมากกว่าหรือเท่ากับ 0').optional(),
		note: z.string().optional(),
		isGuest: z.boolean().default(false),
		promotionId: z.string().optional(),
	})

	const { data: promotions } =
		useQueryGetDepositPromotionNamesForSelectorChannel()

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			promotionId: data.promotionId,
			cost: data.cost,
			note: data.note || '',
			isGuest: data.isGuest || false,
		},
	})

	useEffect(() => {
		form.reset({
			cost: data.cost,
			note: data.note || '',
			isGuest: data.isGuest || false,
			promotionId: data.promotionId,
		})
	}, [data, form])

	const { mutate: updateChannel } = useMutationUpdateChannel({
		onSuccess,
	})
	const queryClient = useQueryClient()

	const onSubmit = async (values: z.infer<typeof formSchema>) => {
		try {
			updateChannel(
				{
					id: data.id,
					payload: {
						cost: values.cost,
						note: values.note?.trim(),
						isGuest: values.isGuest,
						promotionId: values.promotionId,
					},
				},
				{
					onSuccess: () => {
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetChannelList],
						})
						setOpen(false)
						toast.success('อัพเดทช่องทางสำเร็จ')
						form.reset({
							cost: values.cost,
							note: values.note,
							isGuest: values.isGuest,
							promotionId: values.promotionId,
						})
					},
				},
			)
		} catch (error) {
			console.error(error)
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
				<Pencil className="h-4 w-4" />
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>แก้ไขช่องทาง</DialogTitle>
					</DialogHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							<FormField
								control={form.control}
								name="cost"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ต้นทุน</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="ต้นทุน"
												{...field}
												onChange={(e) => field.onChange(Number(e.target.value))}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="note"
								render={({ field }) => (
									<FormItem>
										<FormLabel>หมายเหตุ</FormLabel>
										<FormControl>
											<Input placeholder="กรอกหมายเหตุ (ถ้ามี)" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="isGuest"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel>สำหรับผู้เล่นทดลอง</FormLabel>
											<p className="text-sm text-muted-foreground">
												เลือกว่าช่องทางนี้สำหรับผู้เล่นทดลองหรือไม่
											</p>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="promotionId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>โปรโมชั่น</FormLabel>
										<FormControl>
											<Select
												onValueChange={field.onChange}
												value={field.value}
											>
												<SelectTrigger>
													<SelectValue placeholder="เลือกโปรโมชั่น" />
												</SelectTrigger>
												<SelectContent>
													{promotions?.data.map((promotion) => (
														<SelectItem key={promotion.id} value={promotion.id}>
															{promotion.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex justify-end gap-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setOpen(false)}
								>
									ยกเลิก
								</Button>
								<Button type="submit">บันทึก</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
